import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, type AuditEntry } from "../../api";
import { auditTarget, dateTime } from "../../lib/format";
import { useI18n, type Dict, type Lang } from "../../lib/i18n";
import { downloadCsv, fileStamp, type Cell } from "../../lib/csv";
import { PlanLimitNotice, useFeature, usePlanLimit } from "../../components/PlanNotice";
import { Section, StatusLine, useStatus } from "./common";

/**
 * Who did what to the workspace, newest first. The log itself has been kept
 * since day one; this is only a way to read it. Addresses are shown so an
 * admin can tell two people with the same display name apart.
 *
 * Narrowed by what was done, by whom and between which days — after an
 * incident the question is always "what did this account do last Tuesday",
 * and scrolling for it is not an answer.
 */

const PAGE = 50;
/** How many entries one export may hold. */
export const AUDIT_EXPORT_MAX = 5000;
const EXPORT_PAGE = 200;

interface Filters {
  action: string;
  actorId: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { action: "", actorId: "", from: "", to: "" };

function query(filters: Filters): { action?: string; actorId?: number; from?: string; to?: string } {
  return {
    action: filters.action || undefined,
    actorId: filters.actorId ? Number(filters.actorId) : undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };
}

function actorName(entry: AuditEntry, t: Dict): string {
  return entry.actorPlatform ? t.audit.platform : (entry.actorName ?? t.audit.system);
}

/** The log as spreadsheet rows, header first. */
export function auditCsv(entries: AuditEntry[], t: Dict, lang: Lang): Cell[][] {
  return [
    [t.audit.when, t.audit.who, t.profile.email, t.audit.what, t.audit.targetColumn],
    ...entries.map((entry) => [
      dateTime(entry.createdAt, lang),
      actorName(entry, t),
      entry.actorEmail,
      t.audit.actions[entry.action] ?? entry.action,
      auditTarget(entry, lang),
    ]),
  ];
}

export function AuditTab(): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.audit;
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState<Array<{ id: number; displayName: string; email: string }>>([]);
  const [exporting, setExporting] = useState(false);
  const [status, show, fail] = useStatus();
  const { limit, catchLimit } = usePlanLimit();
  // Below Business the server refuses the read outright (402), so the tab
  // shows the upgrade notice in place of an empty table.
  const canRead = useFeature("auditLog");

  const load = useCallback(
    async (before?: number) => {
      setLoading(true);
      try {
        const res = await api.auditLog({ ...query(filters), before, limit: PAGE });
        setEntries((current) => (before ? [...current, ...res.entries] : res.entries));
        setNextBefore(res.nextBefore);
      } catch (err) {
        if (!catchLimit(err)) fail(err);
      } finally {
        setLoading(false);
      }
    },
    [filters, fail, catchLimit],
  );

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, canRead]);

  useEffect(() => {
    if (!canRead) return;
    void api
      .operators()
      .then((r) => setPeople(r.operators.map((o) => ({ id: o.id, displayName: o.displayName, email: o.email }))))
      .catch(() => undefined);
  }, [canRead]);

  async function exportCsv(): Promise<void> {
    setExporting(true);
    try {
      const collected: AuditEntry[] = [];
      let before: number | undefined;
      let more = true;
      while (more && collected.length < AUDIT_EXPORT_MAX) {
        const res = await api.auditLog({ ...query(filters), before, limit: EXPORT_PAGE });
        collected.push(...res.entries);
        more = res.nextBefore !== null;
        before = res.nextBefore ?? undefined;
      }
      const out = collected.slice(0, AUDIT_EXPORT_MAX);
      downloadCsv(`audit-log-${fileStamp()}.csv`, auditCsv(out, all, lang));
      show("ok", more ? t.exportCapped(out.length) : t.exported(out.length));
    } catch (err) {
      if (!catchLimit(err)) show("error", all.common.exportFailed);
    } finally {
      setExporting(false);
    }
  }

  if (!canRead) {
    return (
      <Section title={all.settings.tabAudit} intro={t.intro}>
        <PlanLimitNotice limit="auditLog" />
      </Section>
    );
  }

  const set = (key: keyof Filters, value: string): void => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <>
      <PlanLimitNotice limit={limit} />
      <StatusLine status={status} />
      <Section title={all.settings.tabAudit} intro={t.intro}>
        <div class="audit-filters">
          <label>
            {t.filter}
            <select value={filters.action} onChange={(e) => set("action", e.currentTarget.value)}>
              <option value="">{t.all}</option>
              {Object.entries(t.actions).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t.actor}
            <select value={filters.actorId} onChange={(e) => set("actorId", e.currentTarget.value)}>
              <option value="">{t.anyone}</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName} ({person.email})
                </option>
              ))}
            </select>
          </label>
          <label>
            {t.from}
            <input type="date" value={filters.from} onInput={(e) => set("from", e.currentTarget.value)} />
          </label>
          <label>
            {t.to}
            <input type="date" value={filters.to} onInput={(e) => set("to", e.currentTarget.value)} />
          </label>
          <button class="btn" disabled={exporting || entries.length === 0} onClick={() => void exportCsv()}>
            {exporting ? all.common.exporting : all.common.exportCsv}
          </button>
        </div>

        {entries.length === 0 && !loading ? (
          <p class="hint">{t.empty}</p>
        ) : (
          <div class="table-wrap">
            <table class="data-table audit">
              <thead>
                <tr>
                  <th scope="col">{t.when}</th>
                  <th scope="col">{t.who}</th>
                  <th scope="col">{t.what}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td class="nowrap">{dateTime(entry.createdAt, lang)}</td>
                    <td>
                      {actorName(entry, all)}
                      {entry.actorEmail ? <span class="hint block">{entry.actorEmail}</span> : null}
                    </td>
                    <td>
                      {t.actions[entry.action] ?? entry.action}
                      {/* The name the server recorded at the time, falling back
                          to the bare id for rows written before it did. */}
                      {auditTarget(entry, lang) ? <span class="hint block">{auditTarget(entry, lang)}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {nextBefore ? (
          <button class="btn" disabled={loading} onClick={() => void load(nextBefore)}>
            {t.more}
          </button>
        ) : null}
      </Section>
    </>
  );
}
